import React, { useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Avatar,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  AppBar,
  Toolbar,
  Backdrop,
  BottomNavigation,
  BottomNavigationAction,
  Container,
  Slide,
  Zoom,
  useMediaQuery as useMediaQueryMui
} from '@mui/material';
import {
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  Notifications as NotificationsIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExitToApp as ExitToAppIcon,
  LightMode as LightModeIcon,
  DarkMode as DarkModeIcon,
  Home as HomeIcon,
  Group as GroupIcon,
  History as HistoryIcon,
  Assessment as AssessmentIcon,
  Tune as TuneIcon,
  NotificationImportant as NotificationImportantIcon,
  CurrencyExchange as CurrencyExchangeIcon,
  Euro as EuroIcon,
  AttachMoney as AttachMoneyIcon,
  Description as DescriptionIcon
} from '@mui/icons-material';
import { Menu as MenuIcon } from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@/store';
import { logoutUser } from '@/store/slices/authSlice';
import api from '@/services/authService';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, AreaChart, Area } from 'recharts';
import AdvancedUserManagement from '@/components/UserManagement/AdvancedUserManagement';
import AnalyticsDashboard from '@/components/Reports/AnalyticsDashboard';
import AdminSettings from '@/components/Settings/AdminSettings';
import ActivityMonitoring from '@/components/Monitoring/ActivityMonitoring';
import BalanceAdjustment from '@/components/BalanceManagement/BalanceAdjustment';
import NotificationCenter from '@/components/Notifications/NotificationCenter';
import CurrencyManagement from '@/components/CurrencyManagement/CurrencyManagement';
// Withdraw and Credit Cards removed from admin sidebar
import LoanApplicationManagement from '@/components/LoanApplicationManagement/LoanApplicationManagement';

interface User {
  id: number;
  username: string;
  email: string;
  fullName: string;
  age?: number;
  address?: string;
  role: string;
  registrationStatus: string;
  createdAt: string;
  approvedAt?: string;
  approvedBy?: number;
  invited?: boolean;
  defaultCurrency?: string;
}

interface SystemStats {
  totalUsers: number;
  pendingUsers: number;
  approvedUsers: number;
  totalTransactions: number;
  totalBalance: number;
  usdBalance: number;
  eurBalance: number;
  transfers: number;
  payments: number;
  adminBalances?: {
    EUR?: number;
    USD?: number;
  };
}

interface Transaction {
  id: number;
  userId: number;
  user?: {
    id: number;
    username: string;
    email: string;
    fullName: string;
  };
  transactionType: 'deposit' | 'withdrawal' | 'transfer';
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed';
  description: string;
  creditCardId?: number;
  createdAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const AdminDashboardModern: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQueryMui(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQueryMui(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();

  const { user } = useSelector((state: RootState) => state.auth);

  const [darkMode, setDarkMode] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const getTabFromPath = (path: string) => {
    if (path.includes('/users')) return 1;
    if (path.includes('/transactions')) return 2;
    if (path.includes('/balance-adjustments')) return 3;
    if (path.includes('/notifications')) return 4;
    if (path.includes('/currencies')) return 5;
    if (path.includes('/loan-applications')) return 6;
    if (path.includes('/analytics')) return 7;
    if (path.includes('/settings')) return 8;
    return 0;
  };
  
  const [tabValue, setTabValue] = useState(() => getTabFromPath(location.pathname));
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [pendingTransactions, setPendingTransactions] = useState<Transaction[]>([]);
  const [pendingLoanApplications, setPendingLoanApplications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  
  const [stats, setStats] = useState<SystemStats>({
    totalUsers: 0,
    pendingUsers: 0,
    approvedUsers: 0,
    totalTransactions: 0,
    totalBalance: 0,
    usdBalance: 0,
    eurBalance: 0,
    transfers: 0,
    payments: 0
  });
  
  const [userGrowthData, setUserGrowthData] = useState<any[]>([]);
  const [transactionData, setTransactionData] = useState<any[]>([]);

  const COLORS = ['#6366f1', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'];

  useEffect(() => {
    let mounted = true;
    let refreshInterval: NodeJS.Timeout;

    const fetchData = async () => {
      try {
        await Promise.all([
          fetchSystemStats(),
          fetchPendingUsers(),
          fetchPendingTransactions(),
          fetchPendingLoanApplications(),
          fetchChartData(),
        ]);
      } catch (err) {
        console.error('Error during data fetch', err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    // Initial fetch
    fetchData();

    // Set up auto-refresh every 5 seconds
    refreshInterval = setInterval(() => {
      if (mounted) {
        fetchData();
      }
    }, 5000);

    return () => {
      mounted = false;
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
  }, []);

  const fetchSystemStats = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const statsResponse = await api.get('/admin/stats');

      if (statsResponse.data.success) {
        const data = statsResponse.data.data;

        // Fetch exchange rates for currency conversion
        let totalBalanceInUSD = 0;
        let exchangeRatesMap: Record<string, number> = {};
        
        // Default exchange rate (EUR to USD) if no rate available
        const DEFAULT_EUR_TO_USD_RATE = 1.15; // Default rate if not set in DB
        
        try {
          const exchangeRatesResponse = await api.get('/admin/currencies/exchange-rates');
          
          console.log('Exchange rates response:', exchangeRatesResponse.data);
          
          // Handle different response structures
          let exchangeRatesArray: any[] = [];
          if (exchangeRatesResponse.data?.data?.exchangeRates) {
            exchangeRatesArray = exchangeRatesResponse.data.data.exchangeRates;
          } else if (Array.isArray(exchangeRatesResponse.data)) {
            exchangeRatesArray = exchangeRatesResponse.data;
          }
          
          console.log('Exchange rates array:', exchangeRatesArray);
          
          // Convert exchange rates array to object for easier lookup
          exchangeRatesArray.forEach((rate: any) => {
            const key = `${rate.fromCurrency}_${rate.toCurrency}`;
            exchangeRatesMap[key] = rate.rate;
          });
          
          console.log('Exchange rates map:', exchangeRatesMap);
        } catch (exchangeErr) {
          console.error('Error fetching exchange rates:', exchangeErr);
          // Continue without exchange rates
        }
        
        // Convert all balances to USD (default currency)
        console.log('Balances before conversion:', data.balances);
        
        Object.entries(data.balances || {}).forEach(([currency, amount]: [string, any]) => {
          const balanceAmount = parseFloat(amount) || 0;
          console.log(`Processing ${currency}: ${balanceAmount}`);
          
          if (currency === 'USD') {
            totalBalanceInUSD += balanceAmount;
            console.log(`Added USD: ${balanceAmount}, Total: ${totalBalanceInUSD}`);
          } else if (currency === 'EUR') {
            // Try to use exchange rate, otherwise use default rate
            const eurToUsdRate = exchangeRatesMap['EUR_USD'] || DEFAULT_EUR_TO_USD_RATE;
            const converted = balanceAmount * eurToUsdRate;
            totalBalanceInUSD += converted;
            console.log(`Converted EUR ${balanceAmount} to USD ${converted} using rate ${eurToUsdRate}, Total: ${totalBalanceInUSD}`);
          } else {
            // For other currencies, add as-is
            totalBalanceInUSD += balanceAmount;
            console.log(`Added ${currency} as-is: ${balanceAmount}, Total: ${totalBalanceInUSD}`);
          }
        });
        
        console.log('Final total balance in USD:', totalBalanceInUSD);

        setStats({
          totalUsers: data.users.total,
          pendingUsers: data.users.pending,
          approvedUsers: data.users.approved,
          totalTransactions: data.transactions.total,
          totalBalance: totalBalanceInUSD,
          usdBalance: data.balances?.USD || 0,
          eurBalance: data.balances?.EUR || 0,
          transfers: data.transactions.transfers || 0,
          payments: data.transactions.payments || 0,
          adminBalances: data.adminBalances || { EUR: 0, USD: 0 }
        });
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const fetchPendingUsers = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.get('/admin/users', {
        params: { status: 'pending' },
      });

      if (response.data.success) {
        setPendingUsers(response.data.data.users);
      }
    } catch (err) {
      setError('Failed to fetch pending users');
    }
  };

  

  const fetchPendingTransactions = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.get('/admin/transactions', {
        params: { status: 'pending' },
      });

      if (response.data.success) {
        setPendingTransactions(response.data.data.transactions);
      }
    } catch (err) {
      setError('Failed to fetch pending transactions');
    }
  };

  const fetchPendingLoanApplications = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.get('/admin/loan-applications', {
        params: { status: 'pending' },
      });

      if (response.data.success) {
        setPendingLoanApplications(response.data.data.loanApplications);
      }
    } catch (err) {
      setError('Failed to fetch pending loan applications');
    }
  };

  const handleApproveLoanApplication = async (loanApplicationId: number) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.put(
        `/admin/loan-applications/${loanApplicationId}/approve`,
        {},
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess('Loan application approved successfully');
        fetchPendingLoanApplications();
        fetchSystemStats();
      } else {
        setError('Failed to approve loan application');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleRejectLoanApplication = async (loanApplicationId: number) => {
    const reason = prompt('Please enter rejection reason:');
    if (!reason) return;

    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.put(
        `/admin/loan-applications/${loanApplicationId}/reject`,
        { reason },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess('Loan application rejected successfully');
        fetchPendingLoanApplications();
      } else {
        setError('Failed to reject loan application');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const fetchChartData = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.get('/admin/analytics');

      if (response.data.success) {
        const data = response.data.data;
        
        // Format user growth data - convert "2026-03" to "Mar"
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const formattedUserGrowth = data.userGrowth.map((item: any) => {
          const date = new Date(item.month);
          const monthName = monthNames[date.getMonth()];
          return {
            name: monthName,
            users: item.newUsers
          };
        });
        setUserGrowthData(formattedUserGrowth);

        // Format transaction volume data
        const formattedTransactionData = data.transactionVolume.map((item: any) => {
          const date = new Date(item.month);
          const monthName = monthNames[date.getMonth()];
          return {
            name: monthName,
            value: item.amount
          };
        });
        setTransactionData(formattedTransactionData);
      }
    } catch (err) {
      console.error('Error fetching chart data:', err);
      // Set default data on error
      setUserGrowthData([
        { name: 'Jan', users: 0 },
        { name: 'Feb', users: 0 },
        { name: 'Mar', users: 0 },
        { name: 'Apr', users: 0 },
        { name: 'May', users: 0 },
        { name: 'Jun', users: 0 }
      ]);
      setTransactionData([
        { name: 'Deposits', value: 0 },
        { name: 'Withdrawals', value: 0 },
        { name: 'Transfers', value: 0 },
        { name: 'Payments', value: 0 }
      ]);
    }
  };

  const handleApproveUser = async (userId: number) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.put(
        `/admin/users/${userId}/approve`,
        {},
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess('User approved successfully');
        fetchPendingUsers();
        fetchSystemStats();
      } else {
        setError('Failed to approve user');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleRejectUser = async (userId: number) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.put(
        `/admin/users/${userId}/reject`,
        { reason: 'Rejected by admin' },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess('User rejected successfully');
        fetchPendingUsers();
        fetchSystemStats();
      } else {
        setError('Failed to reject user');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleApproveTransaction = async (transactionId: number) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.put(
        `/admin/transactions/${transactionId}/approve`,
        { status: 'approved' },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess('Transaction approved successfully');
        fetchPendingTransactions();
        fetchSystemStats();
      } else {
        setError('Failed to approve transaction');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleRejectTransaction = async (transactionId: number) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await api.put(
        `/admin/transactions/${transactionId}/reject`,
        { status: 'rejected', comments: 'Rejected by admin' },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSuccess('Transaction rejected successfully');
        fetchPendingTransactions();
        fetchSystemStats();
      } else {
        setError('Failed to reject transaction');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    }
  };

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/login');
  };

  const dynamicTheme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      primary: {
        main: '#6366f1',
        light: '#818cf8',
        dark: '#4f46e5',
      },
      secondary: {
        main: '#8b5cf6',
        light: '#a78bfa',
        dark: '#7c3aed',
      },
      error: {
        main: '#ef4444',
      },
      warning: {
        main: '#f59e0b',
      },
      success: {
        main: '#10b981',
      },
      background: {
        default: darkMode ? '#0f172a' : '#f8fafc',
        paper: darkMode ? '#1e293b' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#f1f5f9' : '#0f172a',
        secondary: darkMode ? '#94a3b8' : '#64748b',
      },
    },
    typography: {
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      h4: {
        fontWeight: 700,
        letterSpacing: '-0.02em',
      },
      h6: {
        fontWeight: 600,
        letterSpacing: '-0.01em',
      },
      body1: {
        fontSize: '0.95rem',
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.875rem',
        lineHeight: 1.5,
      },
    },
    shape: {
      borderRadius: 16,
    },
    components: {
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            boxShadow: darkMode
              ? '0 4px 20px rgba(0, 0, 0, 0.3)'
              : '0 2px 16px rgba(0, 0, 0, 0.06)',
            overflow: 'hidden',
            transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: darkMode
                ? '0 8px 32px rgba(0, 0, 0, 0.4)'
                : '0 8px 28px rgba(0, 0, 0, 0.1)',
            },
          }
        }
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backdropFilter: 'blur(20px) saturate(180%)',
            background: darkMode
              ? 'rgba(15, 23, 42, 0.85)'
              : 'rgba(255, 255, 255, 0.85)',
          }
        }
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: 'none',
            background: darkMode
              ? 'linear-gradient(180deg, rgba(30, 41, 59, 0.98) 0%, rgba(15, 23, 42, 0.99) 100%)'
              : 'linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, rgba(250, 250, 250, 0.99) 100%)',
            backdropFilter: 'blur(20px) saturate(180%)',
            boxShadow: darkMode
              ? '4px 0 24px rgba(0, 0, 0, 0.3)'
              : '4px 0 24px rgba(0, 0, 0, 0.1)',
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            textTransform: 'none',
            fontWeight: 600,
            padding: '10px 24px',
            transition: 'all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)',
          },
          contained: {
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            boxShadow: '0 4px 14px rgba(99, 102, 241, 0.3)',
            '&:hover': {
              background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
              boxShadow: '0 6px 20px rgba(99, 102, 241, 0.4)',
              transform: 'translateY(-1px)',
            },
          },
          outlined: {
            borderWidth: 1.5,
          },
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            fontWeight: 500,
            fontSize: '0.75rem',
          }
        }
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: 12,
              transition: 'all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1)',
            },
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 24,
            boxShadow: darkMode
              ? '0 24px 80px rgba(0, 0, 0, 0.5)'
              : '0 24px 80px rgba(0, 0, 0, 0.15)',
          }
        }
      },
      MuiBottomNavigation: {
        styleOverrides: {
          root: {
            height: 64,
            background: darkMode
              ? 'rgba(15, 23, 42, 0.95)'
              : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderTop: darkMode ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(0, 0, 0, 0.06)',
          }
        }
      },
    }
  });

  const menuItems = [
    { icon: <HomeIcon />, label: 'Dashboard', id: 'dashboard' },
    { icon: <GroupIcon />, label: 'Users', id: 'users' },
    { icon: <HistoryIcon />, label: 'Activity', id: 'transactions' },
    { icon: <AccountBalanceIcon />, label: 'Balances', id: 'balance-adjustments' },
    { icon: <NotificationImportantIcon />, label: 'Notifications', id: 'notifications' },
    { icon: <CurrencyExchangeIcon />, label: 'Currencies', id: 'currencies' },
    { icon: <DescriptionIcon />, label: 'Loan Applications', id: 'loan-applications' },
    { icon: <AssessmentIcon />, label: 'Analytics', id: 'analytics' },
    { icon: <TuneIcon />, label: 'Settings', id: 'settings' },
  ];

  const drawer = (
    <Box>
      <Toolbar sx={{
        background: darkMode
          ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)'
          : 'linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(139, 92, 246, 0.08) 100%)',
        minHeight: { xs: 56, sm: 64 },
      }}>
        <Typography variant="h6" noWrap component="div" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.1rem' } }}>
          Admin Panel
        </Typography>
      </Toolbar>
      <Divider sx={{ borderColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)' }} />
      <List sx={{ pt: { xs: 1, sm: 2 } }}>
        {menuItems.map((item, index) => (
          <ListItem
            button
            key={item.id}
            selected={tabValue === index}
            onClick={() => {
              setTabValue(index);
              setMobileMenuOpen(false);
            }}
            sx={{
              borderRadius: { xs: 8, sm: 12 },
              mx: { xs: 1, sm: 1.5 },
              mb: 0.5,
              py: { xs: 1, sm: 1.25 },
              transition: 'all 0.2s',
              '&.Mui-selected': {
                background: darkMode ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.1)',
              },
              '&:hover': {
                background: darkMode ? 'rgba(99, 102, 241, 0.2)' : 'rgba(99, 102, 241, 0.08)',
              },
            }}
          >
            <ListItemIcon sx={{
              color: tabValue === index ? '#6366f1' : (darkMode ? '#94a3b8' : '#64748b'),
              minWidth: { xs: 40, sm: 36 },
            }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText
              primary={item.label}
              sx={{
                '& .MuiTypography-root': {
                  fontWeight: 600,
                  fontSize: { xs: '0.95rem', sm: '0.9rem' },
                  color: tabValue === index ? '#6366f1' : (darkMode ? '#94a3b8' : '#64748b'),
                },
              }}
            />
          </ListItem>
        ))}
      </List>
      <Divider sx={{ borderColor: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)', my: 2 }} />
      <List>
        <ListItem
          button
          onClick={handleLogout}
          sx={{
            borderRadius: { xs: 8, sm: 12 },
            mx: { xs: 1, sm: 1.5 },
            py: { xs: 1, sm: 1.25 },
            transition: 'all 0.2s',
            '&:hover': {
              background: darkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
            },
          }}
        >
          <ListItemIcon sx={{ color: '#ef4444', minWidth: { xs: 40, sm: 36 } }}>
            <ExitToAppIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            sx={{
              '& .MuiTypography-root': {
                fontWeight: 600,
                fontSize: { xs: '0.95rem', sm: '0.9rem' },
                color: '#ef4444',
              },
            }}
          />
        </ListItem>
      </List>
    </Box>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: darkMode ? '#0f172a' : '#f8fafc' }}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#6366f1', mb: 2 }} />
          <Typography variant="body2" color="textSecondary" fontWeight="500">Loading admin dashboard...</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <ThemeProvider theme={dynamicTheme}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ display: 'flex', background: darkMode ? '#0f172a' : '#f8fafc', minHeight: '100vh' }}>
          {!isMobile && (
            <AppBar
              position="fixed"
              sx={{
                width: `calc(100% - ${isSmallMobile ? 0 : 260}px)`,
                ml: isSmallMobile ? 0 : '260px',
                bgcolor: 'transparent',
                color: dynamicTheme.palette.text.primary,
                boxShadow: 'none',
                height: 64,
                zIndex: 1100,
              }}
            >
              <Toolbar>
                <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600, fontSize: { xs: '1rem', md: '1.25rem' } }}>
                  Admin Dashboard
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    color="inherit"
                    onClick={() => { fetchSystemStats(); fetchPendingUsers(); }}
                    sx={{
                      background: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      borderRadius: 10,
                    }}
                  >
                    <RefreshIcon />
                  </IconButton>
                  <IconButton
                    color="inherit"
                    onClick={() => setDarkMode(!darkMode)}
                    sx={{
                      background: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      borderRadius: 10,
                    }}
                  >
                    {darkMode ? <LightModeIcon /> : <DarkModeIcon />}
                  </IconButton>
                  <Avatar sx={{
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    width: 40,
                    height: 40,
                  }}>
                    {user?.username?.charAt(0).toUpperCase()}
                  </Avatar>
                </Box>
              </Toolbar>
            </AppBar>
          )}

          {!isMobile && (
            <Box component="nav" sx={{ width: 260, flexShrink: 0, position: 'fixed', height: '100vh', zIndex: 1000 }}>
              <Drawer
                variant="permanent"
                open
                sx={{
                  '& .MuiDrawer-paper': {
                    boxSizing: 'border-box',
                    width: 260,
                    bgcolor: dynamicTheme.palette.background.paper,
                    borderRight: 'none',
                    overflow: 'hidden',
                    height: '100vh',
                    position: 'relative',
                  },
                }}
              >
                {drawer}
              </Drawer>
            </Box>
          )}

          {isMobile && (
            <AppBar
              position="fixed"
              sx={{
                bgcolor: 'transparent',
                color: dynamicTheme.palette.text.primary,
                boxShadow: 'none',
                height: 56,
                zIndex: 1100,
              }}
            >
              <Toolbar sx={{ minHeight: 56 }}>
                <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1, fontWeight: 600, fontSize: '1rem' }}>
                  {menuItems[tabValue]?.label || 'Dashboard'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => { fetchSystemStats(); fetchPendingUsers(); }}
                    sx={{
                      background: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      borderRadius: 8,
                    }}
                  >
                    <RefreshIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setDarkMode(!darkMode)}
                    sx={{
                      background: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      borderRadius: 8,
                    }}
                  >
                    {darkMode ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
                  </IconButton>
                  <IconButton
                    size="small"
                    color="inherit"
                    onClick={() => setMobileMenuOpen(true)}
                    sx={{
                      background: darkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                      borderRadius: 8,
                    }}
                  >
                    <MenuIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Toolbar>
            </AppBar>
          )}

          <Drawer
            anchor="right"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': {
                width: isSmallMobile ? '100%' : 280,
                bgcolor: dynamicTheme.palette.background.paper,
              },
            }}
          >
            {drawer}
          </Drawer>

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              width: { xs: '100%', md: 'calc(100% - 260px)' },
              ml: { xs: 0, md: '260px' },
              mt: { xs: '56px', md: '64px' },
              bgcolor: dynamicTheme.palette.background.default,
              minHeight: '100vh',
              pb: isMobile ? 9 : 0,
              overflow: 'auto',
            }}
          >
            <Slide direction="down" in={!!error}>
              <Box sx={{ px: { xs: 2, md: 3 }, pt: 2 }}>
                {error && (
                  <Alert
                    severity="error"
                    sx={{
                      mb: 2,
                      borderRadius: 12,
                      background: darkMode ? 'rgba(239, 68, 68, 0.15)' : 'rgba(239, 68, 68, 0.1)',
                      '& .MuiAlert-icon': {
                        color: '#ef4444',
                      },
                    }}
                    onClose={() => setError(null)}
                  >
                    {error}
                  </Alert>
                )}
              </Box>
            </Slide>
            <Slide direction="down" in={!!success}>
              <Box sx={{ px: { xs: 2, md: 3 } }}>
                {success && (
                  <Alert
                    severity="success"
                    sx={{
                      mb: 2,
                      borderRadius: 12,
                      background: darkMode ? 'rgba(16, 185, 129, 0.15)' : 'rgba(16, 185, 129, 0.08)',
                      color: '#10b981',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      '& .MuiAlert-icon': {
                        color: '#10b981',
                      },
                    }}
                    onClose={() => setSuccess(null)}
                  >
                    {success}
                  </Alert>
                )}
              </Box>
            </Slide>

            <Container maxWidth={isMobile ? false : 'lg'} disableGutters={isMobile} sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
              {tabValue === 0 && (
                <Box>
                  <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                    <Grid item xs={12} sm={6}>
                      <Zoom in={true} style={{ transitionDelay: '100ms' }}>
                        <Card sx={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: 'white',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
                        }}>
                          <Box sx={{
                            position: 'absolute',
                            top: '-40%',
                            right: '-40%',
                            width: '80%',
                            height: '80%',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                          }} />
                          <CardContent sx={{ position: 'relative', zIndex: 1, py: { xs: 2.5, sm: 3, md: 4 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1.5, md: 2 }}>
                              <Box>
                                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                  User USD Balance
                                </Typography>
                                <Typography variant="h3" fontWeight="800" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                                  ${stats.usdBalance.toLocaleString()}
                                </Typography>
                              </Box>
                              <Avatar sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(12px)',
                                width: { xs: 48, sm: 56 },
                                height: { xs: 48, sm: 56 },
                              }}>
                                <AttachMoneyIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                              </Avatar>
                            </Box>
                            <Chip
                              label="US Dollar"
                              size="small"
                              sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                fontWeight: 600,
                                backdropFilter: 'blur(12px)',
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Zoom in={true} style={{ transitionDelay: '200ms' }}>
                        <Card sx={{
                          background: 'linear-gradient(135deg, #059669 0%, #10b981 100%)',
                          color: 'white',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 8px 32px rgba(5, 150, 105, 0.3)',
                        }}>
                          <Box sx={{
                            position: 'absolute',
                            top: '-40%',
                            right: '-40%',
                            width: '80%',
                            height: '80%',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                          }} />
                          <CardContent sx={{ position: 'relative', zIndex: 1, py: { xs: 2.5, sm: 3, md: 4 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1.5, md: 2 }}>
                              <Box>
                                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                  User EUR Balance
                                </Typography>
                                <Typography variant="h3" fontWeight="800" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                                  €{stats.eurBalance.toLocaleString()}
                                </Typography>
                              </Box>
                              <Avatar sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(12px)',
                                width: { xs: 48, sm: 56 },
                                height: { xs: 48, sm: 56 },
                              }}>
                                <EuroIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                              </Avatar>
                            </Box>
                            <Chip
                              label="Euro"
                              size="small"
                              sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                fontWeight: 600,
                                backdropFilter: 'blur(12px)',
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Zoom in={true} style={{ transitionDelay: '450ms' }}>
                        <Card sx={{
                          background: 'linear-gradient(135deg, #8b5cf6 0%, #6d28d9 100%)',
                          color: 'white',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 8px 32px rgba(139, 92, 246, 0.3)',
                        }}>
                          <Box sx={{
                            position: 'absolute',
                            top: '-40%',
                            right: '-40%',
                            width: '80%',
                            height: '80%',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                          }} />
                          <CardContent sx={{ position: 'relative', zIndex: 1, py: { xs: 2.5, sm: 3, md: 4 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1.5, md: 2 }}>
                              <Box>
                                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                  My EUR Balance
                                </Typography>
                                <Typography variant="h3" fontWeight="800" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                                  {stats.adminBalances?.EUR?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                                </Typography>
                              </Box>
                              <Avatar sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(12px)',
                                width: { xs: 48, sm: 56 },
                                height: { xs: 48, sm: 56 },
                              }}>
                                <EuroIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                              </Avatar>
                            </Box>
                            <Chip
                              label="Personal"
                              size="small"
                              sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                fontWeight: 600,
                                backdropFilter: 'blur(12px)',
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Zoom in={true} style={{ transitionDelay: '460ms' }}>
                        <Card sx={{
                          background: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)',
                          color: 'white',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 8px 32px rgba(6, 182, 212, 0.3)',
                        }}>
                          <Box sx={{
                            position: 'absolute',
                            top: '-40%',
                            right: '-40%',
                            width: '80%',
                            height: '80%',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                          }} />
                          <CardContent sx={{ position: 'relative', zIndex: 1, py: { xs: 2.5, sm: 3, md: 4 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1.5, md: 2 }}>
                              <Box>
                                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                  My USD Balance
                                </Typography>
                                <Typography variant="h3" fontWeight="800" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                                  {stats.adminBalances?.USD?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                                </Typography>
                              </Box>
                              <Avatar sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(12px)',
                                width: { xs: 48, sm: 56 },
                                height: { xs: 48, sm: 56 },
                              }}>
                                <AttachMoneyIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                              </Avatar>
                            </Box>
                            <Chip
                              label="Personal"
                              size="small"
                              sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                fontWeight: 600,
                                backdropFilter: 'blur(12px)',
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Zoom in={true} style={{ transitionDelay: '400ms' }}>
                        <Card sx={{
                          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                          color: 'white',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 8px 32px rgba(99, 102, 241, 0.3)',
                        }}>
                          <Box sx={{
                            position: 'absolute',
                            top: '-40%',
                            right: '-40%',
                            width: '80%',
                            height: '80%',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                          }} />
                          <CardContent sx={{ position: 'relative', zIndex: 1, py: { xs: 2.5, sm: 3, md: 4 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1.5, md: 2 }}>
                              <Box>
                                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                  Total Users
                                </Typography>
                                <Typography variant="h3" fontWeight="800" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                                  {stats.totalUsers}
                                </Typography>
                              </Box>
                              <Avatar sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(12px)',
                                width: { xs: 48, sm: 56 },
                                height: { xs: 48, sm: 56 },
                              }}>
                                <PeopleIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                              </Avatar>
                            </Box>
                            <Chip
                              label="Active"
                              size="small"
                              sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                fontWeight: 600,
                                backdropFilter: 'blur(12px)',
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Zoom in={true} style={{ transitionDelay: '500ms' }}>
                        <Card sx={{
                          background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                          color: 'white',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 8px 32px rgba(245, 158, 11, 0.3)',
                        }}>
                          <Box sx={{
                            position: 'absolute',
                            top: '-40%',
                            right: '-40%',
                            width: '80%',
                            height: '80%',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                          }} />
                          <CardContent sx={{ position: 'relative', zIndex: 1, py: { xs: 2.5, sm: 3, md: 4 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1.5, md: 2 }}>
                              <Box>
                                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                  Pending Approvals
                                </Typography>
                                <Typography variant="h3" fontWeight="800" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                                  {stats.pendingUsers}
                                </Typography>
                              </Box>
                              <Avatar sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(12px)',
                                width: { xs: 48, sm: 56 },
                                height: { xs: 48, sm: 56 },
                              }}>
                                <NotificationsIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                              </Avatar>
                            </Box>
                            <Chip
                              label="Action Required"
                              size="small"
                              sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                fontWeight: 600,
                                backdropFilter: 'blur(12px)',
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Zoom in={true} style={{ transitionDelay: '600ms' }}>
                        <Card sx={{
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: 'white',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.3)',
                        }}>
                          <Box sx={{
                            position: 'absolute',
                            top: '-40%',
                            right: '-40%',
                            width: '80%',
                            height: '80%',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                          }} />
                          <CardContent sx={{ position: 'relative', zIndex: 1, py: { xs: 2.5, sm: 3, md: 4 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1.5, md: 2 }}>
                              <Box>
                                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                  Transfers
                                </Typography>
                                <Typography variant="h3" fontWeight="800" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                                  {stats.transfers}
                                </Typography>
                              </Box>
                              <Avatar sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(12px)',
                                width: { xs: 48, sm: 56 },
                                height: { xs: 48, sm: 56 },
                              }}>
                                <CurrencyExchangeIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                              </Avatar>
                            </Box>
                            <Chip
                              label="Completed"
                              size="small"
                              sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                fontWeight: 600,
                                backdropFilter: 'blur(12px)',
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Zoom in={true} style={{ transitionDelay: '400ms' }}>
                        <Card sx={{
                          background: 'linear-gradient(135deg, #ec4899 0%, #be185d 100%)',
                          color: 'white',
                          position: 'relative',
                          overflow: 'hidden',
                          boxShadow: '0 8px 32px rgba(236, 72, 153, 0.3)',
                        }}>
                          <Box sx={{
                            position: 'absolute',
                            top: '-40%',
                            right: '-40%',
                            width: '80%',
                            height: '80%',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.15) 0%, transparent 70%)',
                          }} />
                          <CardContent sx={{ position: 'relative', zIndex: 1, py: { xs: 2.5, sm: 3, md: 4 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={{ xs: 1.5, md: 2 }}>
                              <Box>
                                <Typography variant="body2" sx={{ opacity: 0.9, fontWeight: 500, mb: { xs: 0.5, md: 1 }, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                                  Payments
                                </Typography>
                                <Typography variant="h3" fontWeight="800" sx={{ fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }, letterSpacing: '-0.02em' }}>
                                  {stats.payments}
                                </Typography>
                              </Box>
                              <Avatar sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                backdropFilter: 'blur(12px)',
                                width: { xs: 48, sm: 56 },
                                height: { xs: 48, sm: 56 },
                              }}>
                                <AccountBalanceIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                              </Avatar>
                            </Box>
                            <Chip
                              label="Deposits & Withdrawals"
                              size="small"
                              sx={{
                                background: 'rgba(255, 255, 255, 0.2)',
                                color: 'white',
                                fontWeight: 600,
                                backdropFilter: 'blur(12px)',
                              }}
                            />
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>

                    <Grid item xs={12}>
                      <Zoom in={true} style={{ transitionDelay: '400ms' }}>
                        <Card sx={{
                          background: darkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                        }}>
                          <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 2, md: 3 }}>
                              <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' } }}>
                                User Growth
                              </Typography>
                              <Chip
                                label="6 Months"
                                size="small"
                                sx={{
                                  background: 'rgba(99, 102, 241, 0.15)',
                                  color: '#6366f1',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            </Box>
                            <Box sx={{ height: { xs: 140, sm: 160, md: 200 } }}>
                              <ResponsiveContainer width="100%" height="100%">
                              {userGrowthData && userGrowthData.length > 0 ? (
                                <AreaChart data={userGrowthData}>
                                  <defs>
                                    <linearGradient id="colorUsers" x1="0" y1="0" x2="0" y2="1">
                                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0, 0, 0, 0.04)'} />
                                  <XAxis
                                    dataKey="name"
                                    stroke={darkMode ? '#94a3b8' : '#64748b'}
                                    fontSize={11}
                                    fontWeight={500}
                                  />
                                  <YAxis
                                    stroke={darkMode ? '#94a3b8' : '#64748b'}
                                    fontSize={11}
                                    fontWeight={500}
                                  />
                                  <RechartsTooltip
                                    contentStyle={{
                                      background: darkMode ? '#1e293b' : '#ffffff',
                                      border: 'none',
                                      borderRadius: 12,
                                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                                      padding: '12px 16px',
                                    }}
                                    itemStyle={{ color: darkMode ? '#f1f5f9' : '#0f172a', fontWeight: 500 }}
                                  />
                                  <Area
                                    type="monotone"
                                    dataKey="users"
                                    stroke="#6366f1"
                                    strokeWidth={2}
                                    fillOpacity={1}
                                    fill="url(#colorUsers)"
                                  />
                                </AreaChart>
                              ) : (
                                <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                                  <Typography variant="body2" sx={{ color: darkMode ? '#94a3b8' : '#64748b', textAlign: 'center' }}>
                                    No user growth data available for the selected period
                                  </Typography>
                                </Box>
                              )}
                            </ResponsiveContainer>
                            </Box>
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>

                    <Grid item xs={12}>
                      <Zoom in={true} style={{ transitionDelay: '500ms' }}>
                        <Card sx={{
                          background: darkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                        }}>
                          <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                            <Typography variant="h6" fontWeight="600" gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' } }}>
                              Transaction Types
                            </Typography>
                            <Box sx={{ height: { xs: 140, sm: 160, md: 200 } }}>
                              <ResponsiveContainer width="100%" height="100%">
                              <RechartsPieChart>
                                <Pie
                                  data={transactionData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={50}
                                  outerRadius={80}
                                  paddingAngle={4}
                                  dataKey="value"
                                  cornerRadius={8}
                                >
                                  {transactionData.map((entry, index) => (
                                    <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <RechartsTooltip
                                  contentStyle={{
                                    background: darkMode ? '#1e293b' : '#ffffff',
                                    border: 'none',
                                    borderRadius: 12,
                                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                                    padding: '12px 16px',
                                  }}
                                  itemStyle={{ color: darkMode ? '#f1f5f9' : '#0f172a', fontWeight: 500 }}
                                />
                              </RechartsPieChart>
                              </ResponsiveContainer>
                              </Box>
                              <Box display="flex" justifyContent="center" mt={2} gap={1.5} flexWrap="wrap">
                              {transactionData.map((entry) => (
                                <Box key={entry.name} display="flex" alignItems="center" sx={{
                                  background: darkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)',
                                  padding: '6px 12px',
                                  borderRadius: 8,
                                }}>
                                  <Box width={8} height={8} bgcolor={COLORS[transactionData.indexOf(entry) % COLORS.length]} borderRadius={2} mr={1} />
                                  <Typography variant="caption" fontWeight="600" fontSize="0.75rem">{entry.name}</Typography>
                                </Box>
                              ))}
                            </Box>
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>

                    <Grid item xs={12}>
                      <Zoom in={true} style={{ transitionDelay: '600ms' }}>
                        <Card sx={{
                          background: darkMode ? 'rgba(30, 41, 59, 0.8)' : 'rgba(255, 255, 255, 0.9)',
                          backdropFilter: 'blur(10px)',
                        }}>
                          <CardContent sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={{ xs: 2, md: 3 }}>
                              <Typography variant="h6" fontWeight="600" sx={{ fontSize: { xs: '1rem', sm: '1.0625rem', md: '1.125rem' } }}>
                                Pending Approvals
                              </Typography>
                              <Chip
                                label={`${pendingUsers.length + pendingTransactions.length + pendingLoanApplications.length} pending`}
                                size="small"
                                sx={{
                                  background: 'rgba(245, 158, 11, 0.15)',
                                  color: '#f59e0b',
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                }}
                              />
                            </Box>
                            {pendingUsers.length === 0 && pendingTransactions.length === 0 && pendingLoanApplications.length === 0 ? (
                              <Box textAlign="center" py={4}>
                                <CheckCircleIcon sx={{ fontSize: 48, color: '#10b981', mb: 2 }} />
                                <Typography variant="body2" color="textSecondary">All caught up! No pending approvals.</Typography>
                              </Box>
                            ) : (
                              <Box display="flex" flexDirection="column" gap={2}>
                                {pendingUsers.length > 0 && (
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1.5, color: darkMode ? '#94a3b8' : '#64748b' }}>
                                      Pending Users
                                    </Typography>
                                    {pendingUsers.map((user) => (
                                      <Box key={user.id} sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: { xs: 2, sm: 2.5 },
                                        borderRadius: { xs: 8, sm: 12 },
                                        background: darkMode ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                          background: darkMode ? 'rgba(245, 158, 11, 0.08)' : 'rgba(0, 0, 0, 0.05)',
                                        },
                                        mb: 1.5,
                                      }}>
                                        <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 2 }}>
                                          <Avatar sx={{
                                            background: 'rgba(245, 158, 11, 0.15)',
                                            color: '#f59e0b',
                                            width: { xs: 36, sm: 40 },
                                            height: { xs: 36, sm: 40 },
                                          }}>
                                            {user.fullName?.charAt(0) || 'U'}
                                          </Avatar>
                                          <Box>
                                            <Typography variant="body1" fontWeight="600" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                                              {user.fullName}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                              {user.email} • {new Date(user.createdAt).toLocaleDateString()}
                                            </Typography>
                                          </Box>
                                        </Box>
                                        <Box display="flex" gap={{ xs: 0.5, sm: 1 }}>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            startIcon={<CheckCircleIcon />}
                                            onClick={() => handleApproveUser(user.id)}
                                            sx={{ borderRadius: 8, fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                          >
                                            Approve
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            startIcon={<CancelIcon />}
                                            onClick={() => handleRejectUser(user.id)}
                                            sx={{ borderRadius: 8, fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                          >
                                            Reject
                                          </Button>
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                                {pendingTransactions.length > 0 && (
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1.5, color: darkMode ? '#94a3b8' : '#64748b' }}>
                                      Pending Transactions
                                    </Typography>
                                    {pendingTransactions.map((transaction) => (
                                      <Box key={transaction.id} sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: { xs: 2, sm: 2.5 },
                                        borderRadius: { xs: 8, sm: 12 },
                                        background: darkMode ? 'rgba(99, 102, 241, 0.08)' : 'rgba(99, 102, 241, 0.05)',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                          background: darkMode ? 'rgba(99, 102, 241, 0.12)' : 'rgba(99, 102, 241, 0.08)',
                                        },
                                        mb: 1.5,
                                      }}>
                                        <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 2 }}>
                                          <Avatar sx={{
                                            background: transaction.transactionType === 'withdrawal' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(16, 185, 129, 0.15)',
                                            color: transaction.transactionType === 'withdrawal' ? '#ef4444' : '#10b981',
                                            width: { xs: 36, sm: 40 },
                                            height: { xs: 36, sm: 40 },
                                          }}>
                                            {transaction.transactionType === 'withdrawal' ? 'W' : 'D'}
                                          </Avatar>
                                          <Box>
                                            <Typography variant="body1" fontWeight="600" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                                              {transaction.transactionType === 'withdrawal' ? 'Withdrawal' : 'Deposit'} - {transaction.user?.fullName || 'Unknown User'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                              {transaction.amount} {transaction.currency} • {new Date(transaction.createdAt).toLocaleDateString()}
                                            </Typography>
                                          </Box>
                                        </Box>
                                        <Box display="flex" gap={{ xs: 0.5, sm: 1 }}>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            startIcon={<CheckCircleIcon />}
                                            onClick={() => handleApproveTransaction(transaction.id)}
                                            sx={{ borderRadius: 8, fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                          >
                                            Approve
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            startIcon={<CancelIcon />}
                                            onClick={() => handleRejectTransaction(transaction.id)}
                                            sx={{ borderRadius: 8, fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                          >
                                            Reject
                                          </Button>
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                                {pendingLoanApplications.length > 0 && (
                                  <Box>
                                    <Typography variant="subtitle2" fontWeight="600" sx={{ mb: 1.5, color: darkMode ? '#94a3b8' : '#64748b' }}>
                                      Pending Loan Applications
                                    </Typography>
                                    {pendingLoanApplications.map((loanApp) => (
                                      <Box key={loanApp.id} sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        p: { xs: 2, sm: 2.5 },
                                        borderRadius: { xs: 8, sm: 12 },
                                        background: darkMode ? 'rgba(139, 92, 246, 0.08)' : 'rgba(139, 92, 246, 0.05)',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                          background: darkMode ? 'rgba(139, 92, 246, 0.12)' : 'rgba(139, 92, 246, 0.08)',
                                        },
                                        mb: 1.5,
                                      }}>
                                        <Box display="flex" alignItems="center" gap={{ xs: 1.5, sm: 2 }}>
                                          <Avatar sx={{
                                            background: 'rgba(139, 92, 246, 0.15)',
                                            color: '#8b5cf6',
                                            width: { xs: 36, sm: 40 },
                                            height: { xs: 36, sm: 40 },
                                          }}>
                                            {loanApp.user?.fullName?.charAt(0) || 'U'}
                                          </Avatar>
                                          <Box>
                                            <Typography variant="body1" fontWeight="600" sx={{ fontSize: { xs: '0.85rem', sm: '0.9rem' } }}>
                                              {loanApp.loanType} - {loanApp.user?.fullName || 'Unknown User'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                                              {loanApp.amount} {loanApp.currency} • {loanApp.paymentMethod ? loanApp.paymentMethod.toUpperCase() : 'N/A'} • {loanApp.createdAt ? new Date(loanApp.createdAt).toLocaleDateString() : 'N/A'}
                                            </Typography>
                                          </Box>
                                        </Box>
                                        <Box display="flex" gap={{ xs: 0.5, sm: 1 }}>
                                          <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            startIcon={<CheckCircleIcon />}
                                            onClick={() => handleApproveLoanApplication(loanApp.id)}
                                            sx={{ borderRadius: 8, fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                          >
                                            Approve
                                          </Button>
                                          <Button
                                            size="small"
                                            variant="outlined"
                                            color="error"
                                            startIcon={<CancelIcon />}
                                            onClick={() => handleRejectLoanApplication(loanApp.id)}
                                            sx={{ borderRadius: 8, fontWeight: 600, fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                                          >
                                            Reject
                                          </Button>
                                        </Box>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Zoom>
                    </Grid>
                  </Grid>
                </Box>
              )}
              {tabValue === 1 && <Box><AdvancedUserManagement /></Box>}
              {tabValue === 2 && <Box><ActivityMonitoring /></Box>}
              {tabValue === 3 && <Box><BalanceAdjustment /></Box>}
              {tabValue === 4 && <Box><NotificationCenter /></Box>}
              {tabValue === 5 && <Box><CurrencyManagement /></Box>}
              {tabValue === 6 && <Box><LoanApplicationManagement /></Box>}
              {tabValue === 7 && <Box><AnalyticsDashboard /></Box>}
              {tabValue === 8 && <Box><AdminSettings /></Box>}
            </Container>
          </Box>

          {isMobile && (
            <BottomNavigation
              value={tabValue}
              onChange={(_, newValue) => {
                setTabValue(newValue);
              }}
              sx={{
                position: 'fixed',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 1200,
                height: 56,
                '& .MuiBottomNavigationAction-root': {
                  minWidth: 'auto',
                  padding: '6px 12px',
                  fontSize: '0.7rem',
                },
              }}
            >
              <BottomNavigationAction label="Home" icon={<HomeIcon />} />
              <BottomNavigationAction label="Users" icon={<GroupIcon />} />
              <BottomNavigationAction label="Activity" icon={<HistoryIcon />} />
              <BottomNavigationAction label="Balances" icon={<AccountBalanceIcon />} />
              <BottomNavigationAction label="More" icon={<MenuIcon />} onClick={() => setMobileMenuOpen(true)} />
            </BottomNavigation>
          )}
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default AdminDashboardModern;
